import React, { useEffect, useRef, useState } from 'react';
import { hours, services, translations } from '../data/mock';
import { Clock, Flame, Fish, Beef, Heart, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const iconMap = {
  Flame: Flame,
  Fish: Fish,
  Beef: Beef,
  Heart: Heart
};

const HoursSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Get current day for highlighting (Monday = 0 in our array)
  const dayIndex = new Date().getDay();
  // Convert Sunday (0) to index 6, others subtract 1
  const todayIndex = dayIndex === 0 ? 6 : dayIndex - 1;

  return (
    <section
      id="orari"
      ref={sectionRef}
      className="py-28 bg-gradient-to-b from-[#e8dcc8] to-[#f0e6d3] relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-64 h-64 bg-[#c9a227]/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-[#7a8450]/15 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block text-[#c9a227] font-semibold tracking-wider uppercase text-sm mb-4">{t.hours.subtitle}</span>
          <h2 className="font-serif text-4xl md:text-6xl font-bold text-[#3d2617] mb-6">
            {t.hours.title}
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-[#c9a227] via-[#c75b39] to-[#c9a227] mx-auto rounded-full"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Hours card */}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-[#e8dcc8]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#c9a227] to-[#8b7119] flex items-center justify-center shadow-lg">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-[#3d2617]">{t.hours.weekly}</h3>
              </div>

              <div className="space-y-3">
                {hours.map((item, index) => {
                  const isToday = index === todayIndex;
                  const displayHours = typeof item.hours === 'string' ? item.hours : item.hours[language];
                  return (
                    <div
                      key={item.day[language]}
                      className={`flex justify-between items-center py-3 px-4 rounded-xl transition-all duration-500 ${
                        isToday 
                          ? 'bg-gradient-to-r from-[#c9a227]/20 to-[#c9a227]/10 border-2 border-[#c9a227] shadow-lg' 
                          : item.closed 
                            ? 'bg-red-50' 
                            : 'hover:bg-[#f5efe0]'
                      }`}
                      style={{ transitionDelay: `${index * 50}ms` }}
                    >
                      <div className="flex items-center gap-3">
                        {isToday && <span className="w-3 h-3 bg-[#c9a227] rounded-full animate-pulse"></span>}
                        <span className={`font-semibold ${isToday ? 'text-[#c9a227]' : item.closed ? 'text-red-600' : 'text-[#3d2617]'}`}>
                          {item.day[language]}
                          {isToday && <span className="text-xs ml-2 opacity-70">({t.hours.today})</span>}
                        </span>
                      </div>
                      <span className={`font-bold ${isToday ? 'text-[#c9a227]' : item.closed ? 'text-red-600' : 'text-[#5c3d2e]'}`}>
                        {displayHours}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Services card */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="bg-[#3d2617] rounded-2xl shadow-2xl p-8 text-white h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-full bg-[#c9a227]/30 flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-[#c9a227]" />
                </div>
                <h3 className="font-serif text-2xl font-bold">{t.hours.services}</h3>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {services.map((service, index) => {
                  const IconComponent = iconMap[service.icon];
                  return (
                    <div
                      key={service.name[language]}
                      className="bg-white/10 rounded-xl p-5 border border-white/10 hover:border-[#c9a227]/50 transition-all duration-300 group hover:scale-105"
                      style={{ transitionDelay: `${index * 100}ms` }}
                    >
                      {IconComponent && <IconComponent className="w-10 h-10 text-[#c9a227] mb-3 group-hover:scale-110 transition-transform" />}
                      <p className="font-semibold">{service.name[language]}</p>
                    </div>
                  );
                })}
              </div>

              {/* Additional info */}
              <div className="bg-white/10 rounded-xl p-6 border border-white/10">
                <h4 className="font-serif text-lg font-bold mb-3 text-[#c9a227]">{t.hours.note}</h4>
                <p className="text-sm text-white/80 leading-relaxed">
                  {t.hours.noteText}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HoursSection;
